import useDuplicateEstimate from '@/api/mutations/estimates/useDuplicateEstimate';
import useUnarchiveEstimate from '@/api/mutations/estimates/useUnarchiveEstimate';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import EstimateStatusBadge from '@/ui/components/estimateStatusBadge/EstimateStatusBadge';
import { ContextMenu, useToastContext } from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ArchiveEstimateModal from './ArchiveEstimateModal';

type EstimateHeaderIdentityProps = {
  projectId: string;
  estimateId: string;
};

const EstimateHeaderIdentity: FC<EstimateHeaderIdentityProps> = (props) => {
  const { projectId, estimateId } = props;

  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  const history = useHistory();
  const { pushSuccess } = useToastContext();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const { mutate: unarchive, isPending: isUnarchiving } = useUnarchiveEstimate({
    projectId,
  });
  const { mutate: duplicate, isPending: isDuplicating } = useDuplicateEstimate({
    projectId,
    options: {
      onSuccess: (data) => {
        pushSuccess('Estimate duplicated');
        history.push(
          `/projects/${projectId}/estimates/${data.duplicateEstimate.id}`
        );
      },
    },
  });

  const isSent = estimate?.status === 'SENT';
  const isArchived = estimate?.status === 'ARCHIVED';
  const canArchive = isSent || estimate?.status === 'DRAFT';

  return (
    <>
      <div className="flex items-center gap-x-2">
        <h4>{estimate?.reference}</h4>

        {estimate?.status && (
          <EstimateStatusBadge
            status={estimate.status}
            sentAt={estimate.sentAt ?? null}
            bouncedAt={estimate.bouncedAt ?? null}
          />
        )}

        <ContextMenu
          size="sm"
          variant="white"
          label="Estimate actions"
          menuItems={[
            {
              label: 'Duplicate estimate',
              disabled: !estimate || isDuplicating,
              onClick: () => duplicate({ id: estimateId }),
            },
            ...(canArchive
              ? [
                  {
                    label: 'Archive estimate',
                    onClick: () => setIsArchiveModalOpen(true),
                  },
                ]
              : []),
            ...(isArchived
              ? [
                  {
                    label: 'Unarchive estimate',
                    disabled: isUnarchiving,
                    onClick: () => unarchive({ id: estimateId }),
                  },
                ]
              : []),
          ]}
        />
      </div>

      <ArchiveEstimateModal
        projectId={projectId}
        estimateId={estimateId}
        isSent={isSent}
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
      />
    </>
  );
};

export default EstimateHeaderIdentity;
